import { showErrorToast } from "@configs/react-toastify/snackbar";
import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import OutlineButton from "@components/Buttons/OutlineButton";
import PrimaryButton from "@components/Buttons/PrimaryButton";
import { ChangePasswordModal } from "@components/Dialogs";
import { CustomCheckbox, CustomDatePicker } from "@components/Forms";
import CustomInput from "@components/Forms/CustomInput";
import ToggleSwitch from "@components/Forms/ToggleSwitch";
import Breadcrumb from "@components/UI/Breadcrumb";
import CustomSelect from "@components/UI/CustomSelect";
import queryKeys from "@configs/react-query/query-key";
import ToastConstant from "@configs/react-toastify/snackbar";
import { CHARACTER_LENGTH, LOCAL_STORAGE, TOAST } from "@constants/index";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { formatDate, getScanToolHelper, translateDepartment } from "@utils/common";
import { getLocalStorage } from "@utils/local-storage";
import validator from 'validator';
import LoadingSpinner from "@components/UI/LoadingSpinner";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";

const TEST_ID = "user-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldInputTestId = (name) => tid(`field-${name}-input`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

const UserDetail = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const isFirstRender = useRef(true);
  const { colors } = useTheme();
  const { t } = useTranslation();
  const currentUserId = getLocalStorage(LOCAL_STORAGE.USER_ID);

  const [isEditMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  const [userData, setUserData] = useState({
    userId: "",
    userName: "",
    roleCd: "",
    roleCdName: "",
    expirationDt: "",
    enableFlag: "",
    deptCd: "",
    deptCdName: "",
    position: "",
    email: "",
    phoneNo: "",
    cellNo: "",
    userMailConfig: {
      binaryVerificationMailFlag: "",
      cveDetectionMailFlag: "",
      sourceCodeVerificationMailFlag: "",
      sbomVerificationMailFlag: "",
      clarityAirVerificationMailFlag: "",
    },
    password: "",
    passwordHash: "",
    companyCd: "",
  });

  const [editFormData, setEditFormData] = useState({
    userId: "",
    userName: "",
    roleCd: "",
    roleCdName: "",
    expirationDt: "",
    enableFlag: "",
    deptCd: "",
    deptCdName: "",
    position: "",
    email: "",
    phoneNo: "",
    cellNo: "",
    userMailConfig: {
      binaryVerificationMailFlag: "",
      cveDetectionMailFlag: "",
      sourceCodeVerificationMailFlag: "",
      sbomVerificationMailFlag: "",
      clarityAirVerificationMailFlag: ""
    },
  });
  const { assignableRolesList, departmentList, scanToolList, fetchSearchInfo } = useSearchInfo();
  const { hasTool } = getScanToolHelper(scanToolList);

  const breadcrumbItems = [
    { label: t("navbar.user-list", "User List"), href: "/user" },
    { label: t("navbar.user-details", "User Details"), href: `/user/${userId}` },
  ];

  const departmentOptions = (departmentList || []).map(item => ({
    value: item.value,
    label: translateDepartment(t, item.value),
  }));

  // Get User Details API
  const getUserDetails = useMutationData(
    queryKeys.getUserDataByUserId + "/" + userId,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setUserData(response);
          setEditFormData(response);
        }
      },
      onError: (error) => {
        if (error?.response?.status === 404) {
          navigate("/404", { state: { backTo: "/user" } });
        } else {
          showErrorToast(t("system.user.user-details-error", "Failed to get user details"));
        }
        console.error("Error during get user details:", error);
      },
    },
  );

  // Update User API
  const updateUser = useMutationData(
    queryKeys.updateUser + "/" + userId,
    {
      method: "PUT",
    },
    {
      onSuccess: async (response) => {
        setLoading(false);
        setEditMode(false);
        // Refresh user data
        getUserDetails.mutate({ userId });
        await updateDataInIndexedDb(STORES.USERS, response, response.userId);
      },
      onError: (error) => {
        console.error("Error during update user:", error);
        const { status, data } = error.response;
        const errorMsg = status === 400 && data ? data : "";
        let subErrMsg = "";
        switch (errorMsg) {
          case "User has reached limit":
            subErrMsg = t("system.user.update-error-limit");
            break;
          case "Duplicate email found":
            subErrMsg = t("system.user.add-error-email-duplicate");
            break;
          case "This user is currently assigned as a Product Manager for one or more Products. Please reassign those Products before changing the user's Role.":
            subErrMsg = t("system.user.update-error-product-manager-assignment");
            break;
          case "This user is currently assigned as a Project Manager for one or more Projects. Please reassign those Projects before changing the user's Role.":
            subErrMsg = t("system.user.update-error-project-manager-assignment");
            break;
          case "This user is currently assigned as a Product Manager for one or more Products. Please reassign those Products before disabling the user.":
            subErrMsg = t("system.user.disable-error-product-manager-assignment");
            break;
          case "This user is currently assigned as a Project Manager for one or more Projects. Please reassign those Projects before disabling the user.":
            subErrMsg = t("system.user.disable-error-project-manager-assignment");
            break;
          default:
            subErrMsg = "";
        }
        ToastConstant(TOAST.error, t("system.user.user-update-fail"), subErrMsg);
        setLoading(false);
      },
    },
  );

  useEffect(() => {
    if (isFirstRender.current) {
      fetchSearchInfo();
      if (userId) {
        getUserDetails.mutate({ userId });
      }
      isFirstRender.current = false;
    }
  }, [fetchSearchInfo, userId, getUserDetails]);

  // Handlers
  const handleInputChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear validation error for this field (and email when mail config changes)
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
    if (field === "userMailConfig") {
      setValidationErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  const handleEditClick = () => {
    setEditFormData(userData);
    setValidationErrors({});
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditFormData(userData);
    setValidationErrors({});
    setEditMode(false);
  };

  const handleChangePassword = () => {
    setShowChangePasswordModal(true);
  };

  const onPasswordChanged = async (newPassword) => {
    getUserDetails.mutate({ userId });
  };

  const isValidEmail = (email) => validator.isEmail(email);
  const isValidPhoneCellNo = (number) => validator.isMobilePhone(number);

  const validateForm = () => {
    const errors = {};

    if (!editFormData.userName?.trim()) {
      errors.userName = "toast.validation.required";
    }
    if (!editFormData.roleCd) {
      errors.roleCd = "toast.validation.required";
    }

    if (!editFormData.email?.trim() || !isValidEmail(editFormData.email)) {
      errors.email = "system.user.email-required";
    }

    if (!isValidPhoneCellNo(editFormData.phoneNo) && editFormData.phoneNo?.trim()) {
      errors.phoneNo = "system.phone-no-invalid";
    }
    if (!isValidPhoneCellNo(editFormData.cellNo) && editFormData.cellNo?.trim()) {
      errors.cellNo = "system.cell-no-invalid";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid = () => {
    const baseValid = !!editFormData.userName?.trim() && !!editFormData.roleCd;
    const emailValid = !!editFormData.email?.trim() && isValidEmail(editFormData.email);
    const noInvalidPhone =
      (editFormData.phoneNo?.trim() ? isValidPhoneCellNo(editFormData.phoneNo) : true) &&
      (editFormData.cellNo?.trim() ? isValidPhoneCellNo(editFormData.cellNo) : true);
    return baseValid && emailValid && noInvalidPhone;
  };

  const handleSaveEdit = () => {
    if (!editFormData.email?.trim() || !isValidEmail(editFormData.email)) {
      ToastConstant(TOAST.error, t("system.user.email-required", "Email is required and must be valid"));
      setValidationErrors((prev) => ({ ...prev, email: "system.user.email-required" }));
      return;
    }
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const bodyRequest = {
      ...userData,
      userId: editFormData.userId,
      userName: editFormData.userName.trim(),
      roleCd: editFormData.roleCd,
      expirationDt: editFormData.expirationDt,
      enableFlag: editFormData.enableFlag,
      deptCd: editFormData.deptCd || "",
      position: editFormData.position || "",
      email: editFormData.email || "",
      phoneNo: editFormData.phoneNo || "",
      cellNo: editFormData.cellNo || "",
      userMailConfig: {
        binaryVerificationMailFlag: editFormData.userMailConfig.binaryVerificationMailFlag,
        cveDetectionMailFlag: editFormData.userMailConfig.cveDetectionMailFlag,
        sourceCodeVerificationMailFlag: editFormData.userMailConfig.sourceCodeVerificationMailFlag,
        sbomVerificationMailFlag: editFormData.userMailConfig.sbomVerificationMailFlag,
        securityAlertMailFlag: editFormData.userMailConfig.securityAlertMailFlag,
        clarityAirVerificationMailFlag: editFormData.userMailConfig.clarityAirVerificationMailFlag,
      },
    };

    updateUser.mutate(bodyRequest, {
      onSuccess: () => {
        ToastConstant(TOAST.success, t("system.user.user-updated-success"));
      },
    });
  };

  const getStatusDisplay = (status) => {
    if (status === "Y") {
      return (
        <div className="flex items-center space-x-2">
          <div className={`h-2 w-2 rounded-full ${colors.bgStatusHealthy}`}></div>
          <span className={`text-sm ${colors.textStatusHealthy}`}>{t("system.user.enable", "Enable")}</span>
        </div>
      );
    }
    return (
      <div className="flex items-center space-x-2">
        <div className={`h-2 w-2 rounded-full ${colors.bgStatusUnhealthy}`}></div>
        <span className={`text-sm ${colors.textDanger}`}>{t("system.user.disable", "Disable")}</span>
      </div>
    );
  };

  const getMailReceptionTags = () => {
    const tags = [];
    if (hasTool("clarity") && userData.userMailConfig?.binaryVerificationMailFlag === "Y") {
      tags.push(t("system.user.clarity-verif-mail-reception", "Clarity Verif. Mail Reception"));
    }
    if (userData.userMailConfig?.cveDetectionMailFlag === "Y") {
      tags.push(t("system.user.cve-detection-mail-reception", "CVE Detection"));
    }
    if (userData.userMailConfig?.securityAlertMailFlag === "Y") {
      tags.push(t("system.user.security-alert-mail-reception", "Security Alert"));
    }
    if (hasTool("fossid") && userData.userMailConfig?.sourceCodeVerificationMailFlag === "Y") {
      tags.push(t("system.user.fossid-verif-mail-reception", "FossID Verif. Mail Reception"));
    }
    if (hasTool("sbom") && userData.userMailConfig?.sbomVerificationMailFlag === "Y") {
      tags.push(t("system.user.sbom-verif-mail-reception", "SBOM Verif. Mail Reception"));
    }
    if (hasTool("clarityair") && userData.userMailConfig?.clarityAirVerificationMailFlag === "Y") {
      tags.push(t("system.user.clarity-air-verif-mail-reception", "ClarityAIR Verif. Mail Reception"));
    }
    return tags;
  };

  if (getUserDetails.isPending) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <div data-testid={tid("page")}>
      {/* Header */}
      <div className={`${colors.bgSecondary} ${colors.borderPrimary}`}>
        <div className="space-y-3">
          <Breadcrumb items={breadcrumbItems} />
          <div>
            <h1
              className={`${colors.textPrimary} text-2xl font-bold break-words leading-tight`}
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
              title={userData.userName || t("system.user.user-details", "User Details")}
            >
              {userData.userName || t("system.user.user-details", "User Details")}
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div data-testid={tid("form")} className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`}>
        <div>
          {/* User Information Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between pb-4">
              <h2 className={`${colors.textPrimary} text-xl font-semibold uppercase`}>
                {t("system.user.user-information", "USER INFORMATION")}
              </h2>
              {isEditMode ? (
                <div className="flex items-center space-x-3">
                  <OutlineButton data-testid={tid("cancel-button")} onClick={handleCancelEdit}>{t("common.cancel-button", "Cancel")}</OutlineButton>
                  <PrimaryButton data-testid={tid("save-button")} onClick={handleSaveEdit} disabled={!isFormValid() || loading}>
                    {loading ? (
                      <div className="flex items-center">
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                        {t("common.saving", "Saving")}
                      </div>
                    ) : (
                      t("common.save-button", "Save")
                    )}
                  </PrimaryButton>
                </div>
              ) : (
                <OutlineButton data-testid={tid("edit-button")} onClick={handleEditClick}>
                  {t("system.user.edit-information", "Edit Information")}
                </OutlineButton>
              )}
            </div>

            {/* Content Grid */}
            <div className="space-y-2 px-4">
              {/* Row 1 */}
              <div className={`grid grid-cols-5 gap-x-4 gap-y-4 border-t ${colors.borderSection} pt-3`}>
                <div className="min-w-0" data-testid={fieldContainerTestId("user-id")}>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("user-id")}>
                    {t("system.user.user-id", "User ID")}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="text"
                      value={editFormData.userId || ""}
                      onChange={(e) => handleInputChange("userId", e.target.value)}
                      disabled
                      className="w-full overflow-hidden text-ellipsis whitespace-nowrap"
                    />
                  ) : (
                    <div className="h-10 flex items-center">
                      <span
                        className={`text-sm ${colors.textPrimary} block w-full overflow-hidden text-ellipsis whitespace-nowrap`}
                        title={userData.userId || "---"}
                        data-testid={fieldValueTestId("user-id")}
                      >
                        {userData.userId}
                      </span>
                    </div>
                  )}
                </div>

                <div className="min-w-0" data-testid={fieldContainerTestId("user-name")}>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("user-name")}>
                    {t("system.user.user-name", "User Name")}
                    {isEditMode && <span className={`ml-1 ${colors.textRequired}`}>*</span>}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("user-name")}
                      type="text"
                      value={editFormData.userName || ""}
                      onChange={(e) => handleInputChange("userName", e.target.value)}
                      maxLength={CHARACTER_LENGTH.TITLE}
                      className="w-full overflow-hidden text-ellipsis whitespace-nowrap"
                    />
                  ) : (
                    <div className="h-10 flex items-center">
                      <span
                        className={`text-sm ${colors.textPrimary} block w-full overflow-hidden text-ellipsis whitespace-nowrap`}
                        title={userData.userName || "---"}
                        data-testid={fieldValueTestId("user-name")}
                      >
                        {userData.userName}
                      </span>
                    </div>
                  )}
                  {validationErrors.userName && (
                    <p className={`${colors.textDanger} text-xs mt-1`}>{t(validationErrors.userName)}</p>
                  )}
                </div>

                <div className="min-w-0" data-testid={fieldContainerTestId("email")}>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("email")}>
                    {t("system.user.role")}
                    {isEditMode && <span className={`ml-1 ${colors.textRequired}`}>*</span>}
                  </label>
                  {isEditMode ? (
                    <CustomSelect
                      options={assignableRolesList}
                      value={assignableRolesList.find((opt) => opt.value === editFormData.roleCd)}
                      onChange={(opt) => handleInputChange("roleCd", opt?.value || null)}
                      placeholder={t("common.please-select")}
                      className="w-full"
                    />
                  ) : (
                    <div className="h-10 flex items-center">
                      <p
                        title={userData?.roleCdName || userData?.roleCd}
                        className={`text-sm ${colors.textPrimary} w-full truncate`}>
                        {userData.roleCdName || userData.roleCd}
                      </p>
                    </div>
                  )}
                  {validationErrors.roleCd && (
                    <p className={`${colors.textDanger} text-xs mt-1`}>{t(validationErrors.roleCd)}</p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("system.user.expiration-date", "Expiration Date")}
                  </label>
                  {isEditMode ? (
                    <CustomDatePicker
                      selected={editFormData.expirationDt ? new Date(editFormData.expirationDt) : null}
                      onChange={(date) => {
                        if (date) {
                          const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                          handleInputChange("expirationDt", normalizedDate.toISOString());
                        } else {
                          handleInputChange("expirationDt", null);
                        }
                      }}
                      placeholderText={t("system.user.expiration-date")}
                      dateFormat="yyyy-MM-dd"
                      minDate={new Date()}
                    />
                  ) : (
                    <p
                      title={formatDate(userData?.expirationDt)}
                      className={`text-sm ${colors.textPrimary} h-10 flex items-center`}>
                      {formatDate(userData.expirationDt)}
                    </p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("system.user.user-status", "User Status")}
                  </label>
                  <div className="h-10 flex items-center">
                    {isEditMode ? (
                      <div className="flex items-center space-x-2">
                        <ToggleSwitch
                          checked={editFormData.enableFlag === "Y"}
                          onChange={(checked) => handleInputChange("enableFlag", checked ? "Y" : "N")}
                          disabled={currentUserId === userId}
                        />
                        <span className={`text-sm ${colors.textPrimary}`}>
                          {editFormData.enableFlag === "Y"
                            ? t("system.user.enable", "Enable")
                            : t("system.user.disable", "Disable")}
                        </span>
                      </div>
                    ) : (
                      getStatusDisplay(userData.enableFlag)
                    )}
                  </div>
                </div>
              </div>

              {/* Row 2 */}
              <div
                className={`grid grid-cols-5 gap-x-4 gap-y-4 pt-3 ${!isEditMode ? `border-t ${colors.borderSection}` : ""}`}
              >
                {/* <div className="min-w-0">
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("system.user.department", "Department")}
                  </label>
                  {isEditMode ? (
                    <CustomSelect
                      options={departmentOptions}
                      value={departmentOptions.find((opt) => opt.value === editFormData.deptCd)}
                      onChange={(opt) => handleInputChange("deptCd", opt?.value || null)}
                      placeholder={t("system.user.department-placeholder")}
                      className="w-full"
                    />
                  ) : (
                    <div className="h-10 flex items-center">
                      <p
                        title={translateDepartment(t, userData?.deptCd || userData?.deptCdName)}
                        className={`text-sm ${colors.textPrimary} w-full truncate`}>
                        {translateDepartment(t, userData?.deptCd) ||
                        userData?.deptCdName ||
                        userData?.deptCd ||
                        "---"}
                      </p>
                    </div>
                  )}
                </div> */}
                <div className="min-w-0">
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("system.user.position", "Position")}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="text"
                      value={editFormData.position || ""}
                      onChange={(e) => handleInputChange("position", e.target.value)}
                      placeholder={t("system.user.position-placeholder")}
                      maxLength={CHARACTER_LENGTH.COMMENT}
                    />
                  ) : (
                    <div className="h-10 flex items-center">
                      <p
                        title={userData?.position}
                        className={`text-sm ${colors.textPrimary} w-full truncate`}>
                        {userData.position || "---"}
                      </p>
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("system.user.email", "Email")}
                    {isEditMode && <span className={`ml-1 ${colors.textRequired}`}>*</span>}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("email")}
                      type="email"
                      value={editFormData.email || ""}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                    />
                  ) : (
                    <div className="h-10 flex items-center">
                      <p title={userData?.email} className={`text-sm ${colors.textPrimary} w-full truncate`} data-testid={fieldValueTestId("email")}>
                        {userData.email || "---"}
                      </p>
                    </div>
                  )}
                  {validationErrors.email && (
                    <div className={`mt-1 text-xs ${colors.textDanger}`}>{t(validationErrors.email)}</div>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("system.user.phone-no", "Phone No.")}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="tel"
                      value={editFormData.phoneNo || ""}
                      onChange={(e) => handleInputChange("phoneNo", e.target.value)}
                    />
                  ) : (
                    <p
                      title={userData?.phoneNo}
                      className={`text-sm ${colors.textPrimary} h-10 flex items-center`}>
                      {userData.phoneNo || "---"}
                    </p>
                  )}
                  {validationErrors.phoneNo && (
                    <div className={`mt-1 text-xs ${colors.textDanger}`}>{t(validationErrors.phoneNo)}</div>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("system.user.cell-no", "Cell. No.")}
                  </label>
                  {isEditMode ? (
                    <CustomInput
                      type="tel"
                      value={editFormData.cellNo || ""}
                      onChange={(e) => handleInputChange("cellNo", e.target.value)}
                    />
                  ) : (
                    <p title={userData?.cellNo} className={`text-sm ${colors.textPrimary} h-10 flex items-center`}>{userData.cellNo || "---"}</p>
                  )}
                  {validationErrors.cellNo && (
                    <div className={`mt-1 text-xs ${colors.textDanger}`}>{t(validationErrors.cellNo)}</div>
                  )}
                </div>
              </div>

              {/* Row 3 */}
              <div
                className={`grid gap-4 pt-3 ${!isEditMode ? `border-t ${colors.borderSection}` : ""}`}
              >
                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("system.user.mail-reception", "Mail Reception")}
                  </label>
                  {isEditMode ? (
                    <div className="flex flex-col space-y-3">
                      {hasTool("clarity") && <CustomCheckbox
                        id="binaryVerificationMailFlag"
                        checked={editFormData.userMailConfig?.binaryVerificationMailFlag === "Y"}
                        onChange={(e) =>
                          handleInputChange("userMailConfig", {
                            ...editFormData.userMailConfig,
                            binaryVerificationMailFlag: e.target.checked ? "Y" : "N",
                          })
                        }
                        label={t("system.user.clarity-verif-mail-reception", "Clarity Verif. Mail Reception")}
                      />}
                      <CustomCheckbox
                        id="cveDetectionMailFlag"
                        checked={editFormData.userMailConfig?.cveDetectionMailFlag === "Y"}
                        onChange={(e) =>
                          handleInputChange("userMailConfig", {
                            ...editFormData.userMailConfig,
                            cveDetectionMailFlag: e.target.checked ? "Y" : "N",
                          })
                        }
                        label={t("system.user.cve-detection-mail-reception", "CVE Detection Mail Reception")}
                      />
                      <CustomCheckbox
                        id="securityAlertMailFlag"
                        checked={editFormData.userMailConfig?.securityAlertMailFlag === "Y"}
                        onChange={(e) =>
                          handleInputChange("userMailConfig", {
                            ...editFormData.userMailConfig,
                            securityAlertMailFlag: e.target.checked ? "Y" : "N",
                          })
                        }
                        label={t("system.user.security-alert-mail-reception", "Security Alert Mail Reception")}
                      />
                      {hasTool("fossid") && <CustomCheckbox
                        id="sourceCodeVerificationMailFlag"
                        checked={editFormData.userMailConfig?.sourceCodeVerificationMailFlag === "Y"}
                        onChange={(e) =>
                          handleInputChange("userMailConfig", {
                            ...editFormData.userMailConfig,
                            sourceCodeVerificationMailFlag: e.target.checked ? "Y" : "N",
                          })
                        }
                        label={t("system.user.fossid-verif-mail-reception", "FossID Verif. Mail Reception")}
                      />}
                      {hasTool("sbom") && <CustomCheckbox
                        id="sbomVerificationMailFlag"
                        checked={editFormData.userMailConfig?.sbomVerificationMailFlag === "Y"}
                        onChange={(e) =>
                          handleInputChange("userMailConfig", {
                            ...editFormData.userMailConfig,
                            sbomVerificationMailFlag: e.target.checked ? "Y" : "N",
                          })
                        }
                        label={t("system.user.sbom-verif-mail-reception", "SBOM Verif. Mail Reception")}
                      />}
                      {hasTool("clarityair") && <CustomCheckbox
                        id="clarityAirVerificationMailFlag"
                        checked={editFormData.userMailConfig?.clarityAirVerificationMailFlag === "Y"}
                        onChange={(e) =>
                          handleInputChange("userMailConfig", {
                            ...editFormData.userMailConfig,
                            clarityAirVerificationMailFlag: e.target.checked ? "Y" : "N",
                          })
                        }
                        label={t("system.user.clarity-air-verif-mail-reception", "ClarityAIR Verif. Mail Reception")}
                      />}
                    </div>
                  ) : (
                    <div className="h-10 flex items-center">
                      <div className="flex flex-wrap gap-2">
                        {getMailReceptionTags().map((tag, index) => (
                          <span
                            key={index}
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.badgeRoleChip}`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`}>
        {/* Password Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between pb-4 uppercase">
            <h2 className={`${colors.textPrimary} text-xl font-semibold`}>{t("system.user.password", "PASSWORD")}</h2>
          </div>

          <div className="space-y-2 px-4">
            <div className="grid grid-cols-5 gap-x-4 gap-y-4">
              <div className="col-span-2">
                <div className="flex">
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("system.user.last-password-change-date")}
                  </label>
                  <label className={`ml-2 block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {formatDate(userData?.passwordChangedAt)}
                  </label>
                </div>

                <div className="mt-1 h-10 flex items-center">
                  <div className="relative">
                    <CustomInput
                      type="password"
                      value={userData.password || "••••••••"}
                      disabled
                      className={`w-full h-10 rounded-lg border px-3 py-2 text-sm font-medium ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} cursor-default`}
                    />
                  </div>
                  <div className="ml-2 flex items-end">
                    <OutlineButton data-testid={tid("change-password-button")} onClick={handleChangePassword}>
                      {t("system.user.change-password", "Change password")}
                    </OutlineButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        user={userData}
        onPasswordChanged={onPasswordChanged}
        requireCurrentPassword={false}
      />
    </div>
  );
};

export default UserDetail;
